import React, { useRef, useState } from 'react';
import { useCommissionDashboard } from '../hooks/useCommissionDashboard';
import { LineItem } from '../types/commission';
import { formatBps, formatCurrency, formatSignedBps } from '../utils/commission';
import { productionTiers } from '../utils/officerPeriod';
import { exportElementToPdf } from '../utils/pdf';
import { CommissionSchedule } from './CommissionSchedule';
import { CompensationLadder } from './CompensationLadder';
import { DashboardHeader } from './DashboardHeader';
import { ExecutiveTierCard } from './ExecutiveTierCard';
import { ExportingIndicator } from './ExportingIndicator';
import { LoanBreakdownTable } from './LoanBreakdownTable';
import { OverrideBpsModal } from './OverrideBpsModal';
import { PeriodAdjustmentCard } from './PeriodAdjustmentCard';
import { QualifyingLoansDrawer } from './QualifyingLoansDrawer';
import { RoleSummaryCard } from './RoleSummaryCard';
import { StatCard } from './StatCard';
import { StatePanel } from './StatePanel';
import { StatusBanner } from './StatusBanner';
import { TierCard } from './TierCard';
import { UnitBonusCard } from './UnitBonusCard';
interface CommissionsWorkspaceProps {
  officerId: string;
  isAdmin: boolean;
  simulateLoadError: boolean;
}

/** One user's full commission statement for the selected pay period. */
export function CommissionsWorkspace({
  officerId,
  isAdmin,
  simulateLoadError
}: CommissionsWorkspaceProps) {
  const d = useCommissionDashboard({
    officerId,
    simulateLoadError
  });
  const [overrideTarget, setOverrideTarget] = useState<LineItem | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const statementRef = useRef<HTMLDivElement>(null);
  const {
    role,
    period,
    officer
  } = d;
  const ready = !d.loading && !d.error;
  const isExecutiveTierCelebration = d.isProductionRole && !role.promotion;
  const rateValue = d.isBps ? formatBps(d.periodRate ?? 0) : formatCurrency(d.step.perUnit ?? 0);
  const rateHelper = d.isBps ? d.adjustment ? `${d.step.totalBps} schedule ${formatSignedBps(d.adjustment.bps)} adjustment` : d.step.bonusBps ? `${role.baseBps} base + ${d.step.bonusBps} bonus` : `${role.baseBps} base · no bonus` : d.step.perUnit ? `per ${role.incentiveNoun ?? 'unit'}` : 'below the first incentive band';
  const loansHeading = `${role.scope === 'Personal' ? 'Funded loans' : `${role.scope} closed units`} · ${period.label}`;
  const lineItemsSubtitle = d.isBps ? `Each loan pays ${formatBps(d.periodRate ?? 0)} of the loan amount${d.adjustment ? `, including a ${formatSignedBps(d.adjustment.bps)} period adjustment` : ''}.` : d.step.perUnit ? `Each unit pays ${formatCurrency(d.step.perUnit)} flat.` : 'No incentive is earned at the current volume.';
  const emptyMessage = period.status === 'open' ? `No payment line items have posted for ${officer.name} yet. Loans appear here as soon as they fund.` : `${officer.name} had no commissionable loans in this pay period.`;
  const qualifyingWindowLabel = d.qualifying ? `${d.qualifying.windowPeriods[d.qualifying.windowPeriods.length - 1].label} – ${d.qualifying.windowPeriods[0].label} · ${d.qualifying.loans.length} funded loans` : null;
  async function handleExport() {
    const node = statementRef.current;
    if (!node || isExporting) return;
    setExportError(null);
    setIsExporting(true);
    try {
      // Let the export-only header state paint before capturing.
      await new Promise(resolve => window.setTimeout(resolve, 80));
      await exportElementToPdf(node, `commission-statement-${officer.id}-${period.id}.pdf`);
    } catch {
      setExportError('We couldn’t generate the PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }
  return <>
      <div ref={statementRef} className="bg-slate-50" data-id="element-186" data-mp="1cm50tl18i0g0">
        <DashboardHeader officer={officer} role={role} periods={d.periods} selectedPeriodId={period.id} onSelectPeriod={d.setSelectedPeriodId} showRecalculate={isAdmin && ready && !d.isLockedPeriod} recalculateSaving={d.recalculateSaving} onRecalculate={d.handleRecalculatePayPeriod} showExport={ready && d.hasData} onExport={handleExport} isExporting={isExporting} data-id="element-187" data-mp="30jw5aamssgsb" />

        <main className="mx-auto max-w-7xl space-y-6 px-6 py-8" data-id="element-188" data-mp="3j8kmomm2o3zc">
          {d.loading ? <StatePanel variant="loading" message="Loading commission dashboard…" data-id="element-189" data-mp="3o8p5wked7ct9" /> : null}

          {!d.loading && d.error ? <StatePanel variant="error" title="We couldn’t load your commissions" message={d.error} onRetry={d.handleRetry} data-id="element-190" data-mp="3ebapulwcsq1h" /> : null}

          {ready ? <>
              {exportError ? <StatusBanner tone="error" data-id="element-191" data-mp="22xgolb273n5t">{exportError}</StatusBanner> : null}
              {d.recalculateError ? <StatusBanner tone="error" data-id="element-192" data-mp="1varxe78ms1bn">{d.recalculateError}</StatusBanner> : null}
              {d.recalculateSuccess ? <StatusBanner tone="success" data-id="element-193" data-mp="1qfmd1cuvmupo">{d.recalculateSuccess}</StatusBanner> : null}

              {!d.hasData ? <StatePanel variant="empty" title={`Nothing to show for ${period.label}`} message={emptyMessage} data-id="element-194" data-mp="20bppbbjg09qc" /> : <>
                  {d.isLockedPeriod ? <StatusBanner tone="locked" data-id="element-195" data-mp="17n1zr3rnuy6z">
                      This pay period is closed and locked. Totals below are the final, audited result.
                    </StatusBanner> : null}

                  {isAdmin && d.isBps && !d.isLockedPeriod && !isExporting ? <PeriodAdjustmentCard key={d.adjustmentKey} current={d.adjustment} loanCount={d.lineItems.length} onApply={d.applyPeriodAdjustment} data-id="element-196" data-mp="1hjdgq7274c7r" /> : null}

                  <section aria-label="Pay period summary" className="grid gap-4 sm:grid-cols-3" data-id="element-197" data-mp="1eilk7j6pus18">
                    <StatCard label={period.status === 'open' ? 'Earned to date' : 'Commission earned'} value={formatCurrency(d.totals.net)} helper={d.totals.capped ? `Capped at ${formatCurrency(role.monthlyCap ?? 0)}` : `${d.lineItems.length} ${d.lineItems.length === 1 ? role.unitNoun : role.unitNounPlural}`} emphasis data-id="element-198" data-mp="1ik6yn8ywfedj" />
                    <StatCard label={role.scope === 'Personal' ? 'Funded units' : `${role.scope} closed units`} value={String(d.units)} helper={`${formatCurrency(d.volume)} volume`} data-id="element-199" data-mp="19m4ni0lgb5cj" />
                    <StatCard label={d.isBps ? 'Earning rate' : 'Incentive rate'} value={rateValue} helper={rateHelper} data-id="element-200" data-mp="2l2l808bv4c69" />
                  </section>

                  <section className="grid items-stretch gap-6 xl:grid-cols-5" data-id="element-201" data-mp="3vshp172datl9">
                    <div className="xl:col-span-2" data-id="element-202" data-mp="3qu6c2ngs02yt">
                      {!d.isProductionRole ? <RoleSummaryCard role={role} units={d.units} activeStepIndex={d.activeStepIndex} requirements={d.requirements} data-id="element-203" data-mp="3ms1lxypjdogx" /> : isExecutiveTierCelebration ? <ExecutiveTierCard role={role} tiers={productionTiers} data-id="element-204" data-mp="37vu4351loz4t" /> : <TierCard role={role} tiers={productionTiers} requirements={d.requirements} onOpenRequirement={d.openQualifyingLoans} data-id="element-205" data-mp="1b1pjam4cd5cn" />}
                    </div>
                    <div className="xl:col-span-3" data-id="element-206" data-mp="jwwp3he9bul6">
                      <UnitBonusCard role={role} units={d.units} volume={d.volume} scorecard={period.scorecard} payingStep={d.step} scorecardMet={d.scorecardMet} isCurrent={period.isCurrent} data-id="element-207" data-mp="66jfzbpmq9u9" />
                    </div>
                  </section>

                  <LoanBreakdownTable key={`${officer.id}-${period.id}`} role={role} items={d.lineItems} heading={loansHeading} subtitle={lineItemsSubtitle} totals={d.totals} showBpsColumn={isAdmin && d.isBps} canEditBps={isAdmin && d.isBps && !d.isLockedPeriod} onEditBps={setOverrideTarget} hideControls={isExporting} data-id="element-208" data-mp="3jduuq6uln7sl" />

                  {d.isProductionRole ? <CompensationLadder tiers={productionTiers} currentRoleId={role.id} activeStepIndex={d.activeStepIndex} data-id="element-209" data-mp="3fvno34f7y3a9" /> : <CommissionSchedule role={role} activeStepIndex={d.activeStepIndex} data-id="element-210" data-mp="3puwfpqmm35tg" />}
                </>}
            </> : null}
        </main>
      </div>

      {overrideTarget ? <OverrideBpsModal item={overrideTarget} onCancel={() => setOverrideTarget(null)} onSave={value => {
      d.saveOverride(overrideTarget.loan.id, value);
      setOverrideTarget(null);
    }} data-id="element-211" data-mp="3tun1th170xrn" /> : null}

      {d.qualifyingRequirement && d.qualifying ? <QualifyingLoansDrawer title={`${officer.name} · ${d.qualifyingRequirement.label}`} windowLabel={qualifyingWindowLabel} loans={d.qualifying.loans} onClose={d.closeQualifyingLoans} data-id="element-212" data-mp="ipkvcj19miqq" /> : null}

      {isExporting ? <ExportingIndicator label={`${officer.name} · ${period.label}`} data-id="element-213" data-mp="4pjo8lueqf2t" /> : null}
    </>;
}